import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { ErrorCardWrapper } from '../../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../../defaultConfig';
import useTranslation, { format, formatJsx } from '../../../hooks/useTranslation';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import { useAvtaleGiroMoveContext } from '../context/AvtaleGiroMoveContext';
import AgreementCheckboxes from './AgreementCheckboxes';

type MoveAvtalegiroStatusType = 'some-error' | 'success' | 'error';

const MoveAvtalegiroStatus: React.FC = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const { avtalegiroLink, frontPageLink } = useConfig();
    const {
        moveAvtaleGiros,
        cancel,
        error: [, setErrorByKey],
        selectedAgreements: [selectedAgreements],
    } = useAvtaleGiroMoveContext();

    const status = useMemo((): MoveAvtalegiroStatusType | null => {
        if (moveAvtaleGiros.isError) return 'error';
        if (moveAvtaleGiros.isSomeError) return 'some-error';
        if (moveAvtaleGiros.isSuccess) return 'success';

        return null;
    }, [moveAvtaleGiros]);

    const cancelAndMoveToFrontPage = useCallback(() => {
        cancel();
        navigate('/');
    }, [cancel, navigate]);

    const moveSelectedAvtalegiros = useCallback(() => {
        setErrorByKey('selectedAgreements', undefined);
        if (selectedAgreements.length < 1) {
            setErrorByKey('selectedAgreements', translated.moveAvtaleGiro.errors.noAvtaleGirosSelected);
            return;
        }
        moveAvtaleGiros.mutate();
    }, [setErrorByKey, selectedAgreements, translated.moveAvtaleGiro.errors.noAvtaleGirosSelected, moveAvtaleGiros]);

    switch (status) {
        case 'error':
            return (
                <ErrorCardWrapper exception={moveAvtaleGiros.error} iconType={RichIconTypes.NETTVERKSBRUDD_HARE}>
                    <ButtonWithSpinner showSpinner={moveAvtaleGiros.isPending} onClick={moveAvtaleGiros.mutate}>
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </ErrorCardWrapper>
            );
        case 'success':
            return (
                <InfoCard skipBackground className="move-avtalegiro__success-wrapper">
                    <RichIcon maxHeight={160} minHeight={160} type={RichIconTypes.FERDIG} />
                    <InfoCard.TitleH3>{translated.moveAvtaleGiro.moveHasStarted}</InfoCard.TitleH3>
                    <p>{translated.moveAvtaleGiro.confirmation}</p>
                    <InfoMessage
                        icon={IconTypes.UTROPSTEGN_24}
                        text={
                            <span className="move-avtalegiro__success-wrapper--info-message">
                                <span className="bold">{translated.moveAvtaleGiro.notaBene}</span>
                                <span>
                                    {formatJsx(
                                        translated.moveAvtaleGiro.someAvtaleGirosMightHaveANewMaxAmount,
                                        <span className="move-avtalegiro__success-wrapper--info-message-italic">
                                            {translated.avtaleGiroAgreement.createTitle}
                                        </span>,
                                    )}
                                </span>
                            </span>
                        }
                    />
                    <section className="move-avtalegiro__action-button-wrapper">
                        {frontPageLink?.url && (
                            <Link linkType="button-positive" href={frontPageLink.url}>
                                {translated.moveAvtaleGiro.toFrontPage}
                            </Link>
                        )}
                        {avtalegiroLink?.url && (
                            <Link linkType="button-negative" href={avtalegiroLink.url}>
                                {translated.moveAvtaleGiro.toAvtaleGiroPage}
                            </Link>
                        )}
                    </section>
                </InfoCard>
            );
        case 'some-error':
            return (
                <section>
                    <div className="move-avtalegiro-form__form-content">
                        <AgreementCheckboxes
                            agreementsToShow={moveAvtaleGiros.data.map((moveResponse) => moveResponse.id.value)}
                        />
                        <ErrorMessage
                            icon={IconTypes.UTROPSTEGN_24}
                            className="move-avtalegiro-form__error-message"
                            text={translated.moveAvtaleGiro.errors.couldNotMoveSomeOrAllAvtaleGiros}
                        />
                    </div>
                    <section className="move-avtalegiro__action-button-wrapper">
                        <ButtonWithSpinner
                            showSpinner={moveAvtaleGiros.isPending}
                            onClick={moveSelectedAvtalegiros}
                            data-track-id={paymentTrackIDs.avtalegiro.moveAvtalegiroError}
                            data-track-linkname={paymentTrackIDs.avtalegiro.moveAvtalegiroErrorName}
                        >
                            {format(translated.moveAvtaleGiro.moveAvtaleGiro, selectedAgreements.length)}
                        </ButtonWithSpinner>
                        <LinkButton
                            onClick={cancelAndMoveToFrontPage}
                            data-track-id={paymentTrackIDs.avtalegiro.moveAvtalegiroError}
                            data-track-linkname={translated.moveAvtaleGiro.toFrontPage}
                        >
                            {translated.moveAvtaleGiro.toFrontPage}
                        </LinkButton>
                    </section>
                </section>
            );
        default:
            return null;
    }
};

export default MoveAvtalegiroStatus;
